import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Clock, FileText, ShieldCheck, XCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth, authHeaders, errCode } from "../context/AuthContext";
import { downloadAuthed } from "../souq";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHIP = { verified: "chip-ok", pending: "chip-warn", rejected: "chip-bad", revoked: "chip-bad", none: "" };
export const isValidCR = (s) => /^[1-9]\d{9}$/.test((s || "").replace(/\s+/g, ""));

export default function TraderVerificationCard() {
  const { t } = useApp();
  const { refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ business_name_ar: "", business_name_en: "", cr_number: "", city: "", note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/trader/verification/me`, { headers: authHeaders() }).then((r) => setInfo(r.data)).catch(() => setInfo({ status: "none" }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!info) return null;

  const crErr = form.cr_number && !isValidCR(form.cr_number);
  const submit = async (e) => {
    e.preventDefault();
    if (crErr) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("document", file);
      await axios.post(`${API}/trader/verification`, fd, { headers: authHeaders() });
      toast.success(t("vt.sent")); load(); refreshUser?.();
    } catch (err) { toast.error(t(`err.${errCode(err)}`)); } finally { setBusy(false); }
  };
  const v = info.verification;
  const canSubmit = ["none", "rejected", "revoked"].includes(info.status);
  const Icon = info.status === "verified" ? BadgeCheck : info.status === "pending" ? Clock : info.status === "none" ? ShieldCheck : XCircle;

  return (
    <section className="ba-card glass-card" data-testid="trader-verification-card">
      <div className="ba-head">
        <Icon size={18} className="ba-ic" />
        <div className="ba-text"><h3 className="dash-panel-title ba-title">{t("vt.title")}</h3><p className="ba-desc">{t("vt.desc")}</p></div>
        <span className={`chip ${CHIP[info.status]} ba-status`} data-testid={`verification-status-${info.status}`}>{t(`vt.status.${info.status}`)}</span>
      </div>
      {v && (
        <dl className="bank-view" data-testid="verification-summary">
          <div><dt>{t("vt.business")}</dt><dd>{v.business_name_ar} / {v.business_name_en}</dd></div>
          <div><dt>{t("vt.cr")}</dt><dd dir="ltr" className="po-mono">{v.cr_number}</dd></div>
          <div><dt>{t("vt.city")}</dt><dd>{v.city}</dd></div>
          {v.has_doc && <div><dt>{t("vt.doc")}</dt><dd><button type="button" className="btn-lux-ghost po-pdf" data-testid="verification-doc-link" onClick={() => downloadAuthed(`${API}/trader/verification/${v.verification_id}/doc`, v.doc_name || "document", authHeaders()).catch(() => toast.error(t("po.dlFail")))}><FileText size={12} /> {v.doc_name}</button></dd></div>}
          {v.reason && info.status !== "verified" && <div className="bank-iban-row"><dt>{t("ba.reason")}</dt><dd className="ba-reason" data-testid="verification-reason">{v.reason}</dd></div>}
        </dl>
      )}
      {canSubmit && (
        <form className="po-form bank-form" onSubmit={submit} data-testid="verification-form">
          <div className="le-grid">
            <label>{t("vt.businessAr")}<input dir="rtl" required minLength={2} value={form.business_name_ar} onChange={(e) => setForm({ ...form, business_name_ar: e.target.value })} data-testid="vt-business-ar" /></label>
            <label>{t("vt.businessEn")}<input dir="ltr" required minLength={2} value={form.business_name_en} onChange={(e) => setForm({ ...form, business_name_en: e.target.value })} data-testid="vt-business-en" /></label>
            <label>{t("vt.cr")}<input dir="ltr" required inputMode="numeric" placeholder="1010XXXXXX" value={form.cr_number} onChange={(e) => setForm({ ...form, cr_number: e.target.value })} className={crErr ? "bank-input-error" : ""} data-testid="vt-cr" />
              {crErr && <span className="bank-error" data-testid="vt-cr-error">{t("err.CR_INVALID")}</span>}</label>
            <label>{t("vt.city")}<input required minLength={2} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="vt-city" /></label>
          </div>
          <label>{t("ba.note")}<textarea rows={2} maxLength={500} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="vt-note" /></label>
          <label>{t("vt.docLabel")}<input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="vt-doc" /><small className="po-dim">{t("vt.docHint")}</small></label>
          <div className="ba-actions"><button type="submit" className="btn-lux-gold" disabled={busy || crErr} data-testid="vt-submit"><ShieldCheck size={14} /> {t("vt.submit")}</button></div>
        </form>
      )}
    </section>
  );
}
